// src/components/JobLocationConfirm.js
//
// "Confirm job location" — sets the job fence used for automatic check-in.
//
// Association addresses are often a leasing office or a street address, not
// the building the work is at. This popup shows the job's address on a
// satellite map with a draggable pin so the scheduler can put the pin on the
// right spot, type in a different address just for this job, or use their own
// phone's location when standing on site. Saving writes jobs/{id}.jobFence
// (see src/clock/jobFence.js). The job's address fields and the property
// record are never changed.
//
//   <JobLocationConfirm job={job} onClose={...} onSaved={(jobFence) => ...} />
//   <JobFenceStatus job={job} canEdit onSaved={(jobFence) => ...} />

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GoogleMap, Marker, Circle, useLoadScript } from "@react-google-maps/api";
import { doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import {
  DEFAULT_FENCE_RADIUS_M,
  FENCE_RADIUS_OPTIONS,
  getJobFence,
  metersToFeet,
} from "../clock/jobFence";
import { getLocation } from "../clock/useClockInLogic";
import "./JobLocationConfirm.css";

const mapStyle = { width: "100%", height: "320px" };

const jobAddress = (job) =>
  [job?.address, job?.city, job?.state, job?.zip].filter(Boolean).join(", ");

const geocode = (address) =>
  new Promise((resolve) => {
    const Geocoder = window.google?.maps?.Geocoder;
    if (!Geocoder || !address) return resolve(null);
    try {
      new Geocoder().geocode({ address }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          resolve({
            latitude: loc.lat(),
            longitude: loc.lng(),
            formatted: results[0].formatted_address || address,
            // ROOFTOP is a real building match; anything else is a street or area.
            precise: results[0].geometry.location_type === "ROOFTOP",
          });
        } else {
          resolve(null);
        }
      });
    } catch {
      resolve(null);
    }
  });

const whoAmI = () => {
  const u = getAuth().currentUser;
  return { name: u?.displayName || u?.email || "", uid: u?.uid || "" };
};

// ---------------------------------------------------------------------------
export default function JobLocationConfirm({ job, onClose, onSaved, heading }) {
  // Same options as every other map in the app, so the loader is shared.
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const existing = useMemo(() => getJobFence(job), [job]);
  const onFile = jobAddress(job);

  const [pin, setPin] = useState(
    existing ? { latitude: existing.latitude, longitude: existing.longitude } : null
  );
  const [radius, setRadius] = useState(existing?.radiusMeters || DEFAULT_FENCE_RADIUS_M);
  const [autoCheckIn, setAutoCheckIn] = useState(existing ? existing.autoCheckIn : true);
  const [useCustom, setUseCustom] = useState(existing?.addressSource === "custom");
  const [customAddress, setCustomAddress] = useState(
    existing?.addressSource === "custom" ? existing.address || "" : ""
  );
  const [addressSource, setAddressSource] = useState(existing?.addressSource || "job");
  const [pinAdjusted, setPinAdjusted] = useState(!!existing?.pinAdjusted);
  const [lookupNote, setLookupNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // First open with no fence yet: drop the pin on the job's address.
  useEffect(() => {
    if (!isLoaded || pin || !onFile) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      const g = await geocode(onFile);
      if (cancelled) return;
      setBusy(false);
      if (g) {
        setPin({ latitude: g.latitude, longitude: g.longitude });
        setAddressSource("job");
        setLookupNote(
          g.precise
            ? "Found the building for this address. Check the pin is on the right spot."
            : "This address only matched a street or general area. Drag the pin to where the work is."
        );
      } else {
        setLookupNote("Could not find this address on the map. Type a different address or tap the map.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, pin, onFile]);

  const findCustom = async () => {
    if (!customAddress.trim()) return;
    setBusy(true);
    setError("");
    const g = await geocode(customAddress.trim());
    setBusy(false);
    if (!g) {
      setError("That address was not found. Check the spelling, or tap the map to place the pin.");
      return;
    }
    setPin({ latitude: g.latitude, longitude: g.longitude });
    setAddressSource("custom");
    setPinAdjusted(false);
    setLookupNote(
      g.precise
        ? `Found: ${g.formatted}`
        : `Found the general area for ${g.formatted}. Drag the pin onto the building.`
    );
  };

  const useMyLocation = async () => {
    setBusy(true);
    setError("");
    try {
      const gps = await getLocation();
      setPin({ latitude: gps.latitude, longitude: gps.longitude });
      setAddressSource("device");
      setPinAdjusted(false);
      setLookupNote(
        `Pin placed at your phone's location${
          gps.accuracy ? ` (accurate to about ${Math.round(gps.accuracy)} m)` : ""
        }.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const movePin = useCallback((e) => {
    const lat = e?.latLng?.lat?.();
    const lng = e?.latLng?.lng?.();
    if (lat == null || lng == null) return;
    setPin({ latitude: lat, longitude: lng });
    setPinAdjusted(true);
  }, []);

  const save = async () => {
    if (!job?.id) return;
    if (!pin) {
      setError("Place the pin first: tap the map, type an address, or use your location.");
      return;
    }
    setSaving(true);
    setError("");
    const me = whoAmI();
    const jobFence = {
      latitude: pin.latitude,
      longitude: pin.longitude,
      radiusMeters: Number(radius) || DEFAULT_FENCE_RADIUS_M,
      address: addressSource === "custom" ? customAddress.trim() : onFile,
      addressSource,
      pinAdjusted,
      autoCheckIn,
      confirmed: true,
      confirmedAt: new Date().toISOString(),
      confirmedBy: me.name,
      confirmedByUid: me.uid,
    };
    if (existing?.carriedFromJobId) jobFence.carriedFromJobId = existing.carriedFromJobId;
    try {
      await updateDoc(doc(db, "jobs", job.id), { jobFence });
      onSaved?.(jobFence);
      onClose?.();
    } catch (err) {
      console.error("Saving job fence failed:", err);
      setError("Could not save the job location. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const center = pin ? { lat: pin.latitude, lng: pin.longitude } : { lat: 38.8048, lng: -77.0469 };
  const carried = existing?.carriedFromJobId;

  return (
    <div className="jlc-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="jlc-modal" role="dialog" aria-modal="true">
        <h3 className="jlc-title">{heading || "Confirm job location"}</h3>
        <div className="jlc-job">
          <strong>{job?.siteName || "Job"}</strong>
          {job?.wo ? <span className="jlc-muted"> · WO #{job.wo}</span> : null}
        </div>
        <p className="jlc-help">
          This sets the check-in area for this job only. Techs assigned to it are checked in
          automatically when they arrive inside the circle and checked out when they leave.
          {carried ? " This location was carried over from the job this was continued from. Is it still right?" : ""}
        </p>

        <div className="jlc-row">
          <span className="jlc-label">Address on file</span>
          <span>{onFile || <em>None</em>}</span>
        </div>

        <label className="jlc-check">
          <input
            type="checkbox"
            checked={useCustom}
            onChange={(e) => setUseCustom(e.target.checked)}
          />
          Use a different address for this job
        </label>
        {useCustom && (
          <div className="jlc-custom">
            <input
              type="text"
              value={customAddress}
              placeholder="e.g. 1200 Oak Ridge Ct, Building 4, Alexandria, VA"
              onChange={(e) => setCustomAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  findCustom();
                }
              }}
            />
            <button type="button" className="jlc-btn" onClick={findCustom} disabled={busy}>
              Find
            </button>
          </div>
        )}

        <div className="jlc-map">
          {loadError ? (
            <div className="jlc-error">The map could not load. Check your connection and try again.</div>
          ) : !isLoaded ? (
            <div className="jlc-muted">Loading map…</div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapStyle}
              center={center}
              zoom={pin ? 18 : 11}
              mapTypeId="hybrid"
              onClick={movePin}
              options={{ streetViewControl: false, fullscreenControl: false, tilt: 0 }}
            >
              {pin && (
                <>
                  <Marker
                    position={center}
                    draggable
                    onDragEnd={movePin}
                    title="Drag to the spot where the work is"
                  />
                  <Circle
                    center={center}
                    radius={Number(radius)}
                    options={{
                      strokeColor: "#16a34a",
                      strokeOpacity: 0.95,
                      strokeWeight: 2,
                      fillColor: "#16a34a",
                      fillOpacity: 0.18,
                      clickable: false,
                    }}
                  />
                </>
              )}
            </GoogleMap>
          )}
        </div>
        <div className="jlc-tip">Tap the map or drag the pin to move it.</div>
        {lookupNote && <div className="jlc-note">{lookupNote}</div>}

        <div className="jlc-controls">
          <label>
            Fence size{" "}
            <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
              {FENCE_RADIUS_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m} m (~{metersToFeet(m)} ft)
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="jlc-btn ghost" onClick={useMyLocation} disabled={busy}>
            {busy ? "Working…" : "Use my current location"}
          </button>
        </div>
        <div className="jlc-tip">
          Phone GPS is usually off by 15–50 m, so very small fences cause false check-outs.
          75 m suits most buildings; go bigger for large grounds.
        </div>

        <label className="jlc-check">
          <input
            type="checkbox"
            checked={autoCheckIn}
            onChange={(e) => setAutoCheckIn(e.target.checked)}
          />
          Auto check-in / check-out for this job
        </label>

        {error && <div className="jlc-error">{error}</div>}

        <div className="jlc-actions">
          <button type="button" className="jlc-btn primary" onClick={save} disabled={saving || busy}>
            {saving ? "Saving…" : "Confirm location"}
          </button>
          <button type="button" className="jlc-btn ghost" onClick={onClose} disabled={saving}>
            {existing?.confirmed ? "Cancel" : "Skip for now"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One-line status for job popups: "Job location: Confirmed · 75 m · auto on"
// plus a button for staff to confirm / change it.
export function JobFenceStatus({ job, canEdit = false, onSaved }) {
  const [open, setOpen] = useState(false);
  if (!job?.id) return null;
  const fence = getJobFence(job);
  const confirmed = !!fence?.confirmed;

  let text;
  if (!confirmed) text = "Not confirmed — no automatic check-in";
  else if (!fence.autoCheckIn) text = `Confirmed · ${fence.radiusMeters} m · auto check-in off`;
  else text = `Confirmed · ${fence.radiusMeters} m (~${metersToFeet(fence.radiusMeters)} ft) · auto check-in on`;

  return (
    <div className={`jlc-status ${confirmed ? "ok" : "todo"}`} onClick={(e) => e.stopPropagation()}>
      <span>
        📍 <strong>Job location:</strong> {text}
        {confirmed && fence.addressSource === "custom" && fence.address ? (
          <span className="jlc-muted"> · uses {fence.address}</span>
        ) : null}
      </span>
      {canEdit && (
        <button type="button" className="jlc-btn small" onClick={() => setOpen(true)}>
          {confirmed ? "Change" : "Confirm location"}
        </button>
      )}
      {open && (
        <JobLocationConfirm job={job} onClose={() => setOpen(false)} onSaved={onSaved} />
      )}
    </div>
  );
}
